import { Component, OnInit } from '@angular/core';
import {ActivatedRoute, Router} from "@angular/router";
import {GameService} from "../../services/game.service";
import { Meta, Title } from "@angular/platform-browser";


@Component({
  selector: 'app-game-detail',
  templateUrl: './game-detail.component.html',
  styleUrls: ['./game-detail.component.css']
})
export class GameDetailComponent implements OnInit {
  gameId: number | null = null;
  gameDetails: any;
  clickCount: number | null = null;
  errorMessage: string | null = null;
  popularGames: any[] = [];
  shareMessage: string | null = null;

  constructor(
    private route: ActivatedRoute,
    private _gameService: GameService,
    private router: Router,
    private title: Title,
    private meta: Meta
  ) {}

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      this.gameId = this.parseGameId(params.get('id'));
      if (this.gameId === null) {
        this.errorMessage = 'Invalid game link.';
        return;
      }
      this.getGameDetails(this.gameId);
    });
    this.loadPopular();
  }

  parseGameId(rawId: string | null): number | null {
    if (!rawId) return null;
    const decoded = decodeURIComponent(rawId);
    const match = decoded.match(/^\d+/);
    if (!match) return null;
    const parsed = Number(match[0]);
    return Number.isFinite(parsed) ? parsed : null;
  }

  getGameSlug(name: string): string {
    return (name || 'game')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  getGameDetails(gameId: number) {
    this._gameService.getGameById(gameId).subscribe((data) => {
      this.gameDetails = data;
      this.updateCanonicalRoute();
      this.updateSocialMeta();
    });
  }

  loadPopular(limit: number = 4) {
    this._gameService.getGamesLimited(limit).subscribe({
      next: (data) => (this.popularGames = data?.length ? data.slice(0, limit) : []),
      error: () => {
        // Fallback: load all and slice locally
        this._gameService.getGames().subscribe(all => (this.popularGames = (all || []).slice(0, limit)));
      },
    });
  }

  routeToPage(page: string) {
    this.router.navigate([page]);
  }

  onGameClick(): void {
    if (this.gameId === null) return;
    this._gameService.incrementClickCount(this.gameId).subscribe(
      () => {
        if (this.clickCount !== null) {
          this.clickCount++;
        }
      },
      () => (this.errorMessage = '')
    );
  }

  openGame(game: any) {
    if (!game || !game.id) return;
    const slug = this.getGameSlug(game.name || '');
    this.router.navigate(['/game', game.id, slug]);
  }

  getShareUrl(): string {
    const gamePath = this.router.serializeUrl(
      this.router.createUrlTree(['/game', this.gameId, this.getGameSlug(this.gameDetails?.name || '')])
    );
    return `${window.location.origin}${gamePath}`;
  }

  async shareGame(): Promise<void> {
    if (!this.gameId) return;

    const shareUrl = this.getShareUrl();
    const shareTitle = this.gameDetails?.name || 'Check out this game';
    const shareText = `Play ${shareTitle} on YoGamez Hub`;

    try {
      if (navigator.share) {
        await navigator.share({ title: shareTitle, text: shareText, url: shareUrl });
        this.shareMessage = 'Share opened.';
        return;
      }

      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
        this.shareMessage = 'Link copied to clipboard.';
        return;
      }

      this.shareMessage = `Copy this link: ${shareUrl}`;
    } catch {
      this.shareMessage = 'Unable to share right now. Please try again.';
    }
  }

  updateCanonicalRoute(): void {
    if (!this.gameId || !this.gameDetails?.name) return;
    const canonicalPath = this.router.serializeUrl(
      this.router.createUrlTree(['/game', this.gameId, this.getGameSlug(this.gameDetails.name)])
    );
    if (this.router.url !== canonicalPath) {
      this.router.navigateByUrl(canonicalPath, { replaceUrl: true });
    }
  }

  updateSocialMeta(): void {
    const gameName = this.gameDetails?.name || 'Game';
    const description = `Play ${gameName} on YoGamez Hub.`;
    const url = this.getShareUrl();
    const image = this.gameDetails?.image ? `data:image/png;base64,${this.gameDetails.image}` : '';

    this.title.setTitle(`${gameName} | YoGamez Hub`);
    this.meta.updateTag({ name: 'description', content: description });
    this.meta.updateTag({ property: 'og:title', content: `${gameName} | YoGamez Hub` });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({ property: 'og:url', content: url });
    if (image) this.meta.updateTag({ property: 'og:image', content: image });
    this.meta.updateTag({ name: 'twitter:card', content: image ? 'summary_large_image' : 'summary' });
    this.meta.updateTag({ name: 'twitter:title', content: `${gameName} | YoGamez Hub` });
    this.meta.updateTag({ name: 'twitter:description', content: description });
    if (image) this.meta.updateTag({ name: 'twitter:image', content: image });
  }
}
